import { Link, useRouterState } from "@tanstack/react-router";
import {
  ListTodo,
  MapPin,
  MessageSquare,
  Package,
  Receipt,
  Tag,
  Users,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "~/components/ui/sidebar";
import { BrandMark } from "~/components/brand-mark";
import { NavUser } from "~/components/shell/nav-user";
import { useMe, isAdmin } from "~/lib/auth";
import { useEffect } from "react";

type NavItem = { title: string; url: string; icon: typeof Package; adminOnly?: boolean; exact?: boolean };

const WORK: NavItem[] = [
  { title: "Today", url: "/ops", icon: ListTodo, exact: true },
  { title: "Parcels", url: "/ops/parcels", icon: Package },
  { title: "Transactions", url: "/ops/transactions", icon: Receipt },
  { title: "Messages", url: "/ops/messages", icon: MessageSquare },
];

const CONFIG: NavItem[] = [
  { title: "Shipping addresses", url: "/ops/addresses", icon: MapPin },
  { title: "Freighter rates", url: "/ops/rates", icon: Tag, adminOnly: true },
  { title: "Users & roles", url: "/ops/users", icon: Users, adminOnly: true },
];

function isActive(path: string, item: NavItem): boolean {
  return item.exact ? path === item.url : path === item.url || path.startsWith(`${item.url}/`);
}

export function AppSidebar() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const { isMobile, setOpenMobile } = useSidebar();

  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [path, isMobile, setOpenMobile]);

  const groups = [
    { label: "Operations", items: WORK },
    { label: "Configuration", items: CONFIG.filter((i) => !i.adminOnly || admin) },
  ];

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex h-9 items-center gap-2 px-1.5">
          <BrandMark size={20} />
          <span className="text-[14px] font-semibold tracking-[-0.01em] group-data-[collapsible=icon]:hidden">Ship Ops</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {groups.map((g) => (
          <SidebarGroup key={g.label}>
            <SidebarGroupLabel>{g.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {g.items.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild isActive={isActive(path, item)} tooltip={item.title}>
                      <Link to={item.url}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <NavUser />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
